import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import API from '../../config/axiosConfig';
import { Button, Grid, Typography, Paper, MenuItem } from '@mui/material';
import CommonLayout from '../../components/CommonLayout';
import Loader from '../../components/Loader';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import PageTitle from '../../components/PageTitle';
import { X } from 'lucide-react';
import { primaryButtonStyle, secondaryButtonStyle } from '../../utils/sharedStyles';
import InputWithIcon from '../../components/InputWithIcon';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import keyIcon from '../../assets/protocols/protocol.svg'; // Using existing icons for now
import titleIcon from '../../assets/protocols/protocol.svg';
import iconIcon from '../../assets/protocols/protocol.svg';
import creditIcon from '../../assets/protocols/author.svg';
import statusIcon from '../../assets/protocols/tags.svg';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import userIcon from '../../assets/users/user.svg';

const AddEditApp = () => {
  const { user, hospitals } = useContext(UserContext);
  const [app, setApp] = useState({
    key: '',
    appToken: '',
    title: '',
    icon: '',
    description: '',
    credit: '',
    status: 'normal',
    admins: [],
  });
  const [usersByHospital, setUsersByHospital] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      fetchApp();
    }
  }, [id]);

  const fetchApp = async () => {
    setIsLoading(true);
    try {
      const response = await API.get(`/api/apps/${id}`);
      setApp(response.data);
      response.data.admins.forEach(admin => {
        if (admin.hospital) {
          fetchUsersForHospital(admin.hospital._id);
        }
      });
    } catch (error) {
      console.error('Fetching app failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsersForHospital = async (hospitalId) => {
    if (!hospitalId || usersByHospital[hospitalId]) return;
    try {
      const response = await API.get(`/api/users?hospital=${hospitalId}`);
      setUsersByHospital(prev => ({ ...prev, [hospitalId]: response.data }));
    } catch (error) {
      console.error(`Fetching users for hospital ${hospitalId} failed:`, error);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!app.key.trim()) newErrors.key = 'מפתח הוא שדה חובה';
    if (!app.appToken.trim()) newErrors.appToken = 'App Token הוא שדה חובה';
    if (!app.title.trim()) newErrors.title = 'כותרת היא שדה חובה';
    if (!app.icon.trim()) newErrors.icon = 'אייקון הוא שדה חובה';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const appData = { ...app };
      appData.admins = app.admins.map(admin => ({
        user: admin.user?._id || admin.user,
        hospital: admin.hospital?._id || admin.hospital,
      }));

      const endpoint = id ? `/api/apps/${id}` : '/api/apps';
      const method = id ? 'put' : 'post';
      await API[method](endpoint, appData);
      navigate('/apps-mgmt');
    } catch (error) {
      console.error('Submitting app failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdminChange = (index, field, value) => {
    const newAdmins = [...app.admins];
    newAdmins[index][field] = value;
    if (field === 'hospital') {
      newAdmins[index].user = ''; // Reset user when hospital changes
      fetchUsersForHospital(value);
    }
    setApp(prev => ({ ...prev, admins: newAdmins }));
  };

  const addAdmin = () => {
    setApp(prev => ({ ...prev, admins: [...prev.admins, { hospital: '', user: '' }] }));
  };

  const removeAdmin = (index) => {
    const newAdmins = [...app.admins];
    newAdmins.splice(index, 1);
    setApp(prev => ({ ...prev, admins: newAdmins }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB');
  };

  return (
    <CommonLayout>
      <div className="add-edit-app-container" style={{ direction: 'rtl' }}>
        <PageTitle title={id ? 'עריכת אפליקציה' : 'יצירת אפליקציה'} />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
           {id && (
             <>
               <Grid item xs={12} sm={4}>
                 <Typography>תאריך הוספה: {formatDate(app.dateAdded)}</Typography>
               </Grid>
               <Grid item xs={12} sm={4}>
                 <Typography>עדכון אחרון: {formatDate(app.lastUpdatedAt)}</Typography>
               </Grid>
               <Grid item xs={12} sm={4}>
                 <Typography>עודכן לאחרונה על ידי: {app.lastUpdatedBy?.name || 'N/A'}</Typography>
               </Grid>
             </>
           )}
           <Grid item xs={12} sm={6}>
              <InputWithIcon
                icon={keyIcon}
                placeholder="מפתח"
                value={app.key}
                onChange={(e) => setApp({ ...app, key: e.target.value })}
                onBlur={(e) => {
                  const newKey = e.target.value;
                  if (app.appToken.trim() === '' || app.appToken.trim() === app.key.toLowerCase().replace(/_/g, '-')) {
                    const newAppToken = newKey.toLowerCase().replace(/_/g, '-');
                    setApp(prevApp => ({ ...prevApp, appToken: newAppToken }));
                  }
                }}
                error={!!errors.key}
                helperText={errors.key}
                required
                disabled={!!id}
              />
               <Typography variant="caption" display="block" gutterBottom>
                   המפתח צריך להיות מזהה חד-חד-ערכי של האפליקציה
               </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputWithIcon
                icon={keyIcon}
                placeholder="נתיב"
                value={app.appToken}
                onChange={(e) => setApp({ ...app, appToken: e.target.value })}
                error={!!errors.appToken}
                helperText={errors.appToken}
                required
              />
               <Typography variant="caption" display="block" gutterBottom>
                   הנתיב ב URL דרכו יגשו לאפליקציה
               </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputWithIcon
                icon={titleIcon}
                placeholder="כותרת"
                value={app.title}
                onChange={(e) => setApp({ ...app, title: e.target.value })}
                error={!!errors.title}
                helperText={errors.title}
                required
              />
               <Typography variant="caption" display="block" gutterBottom>
                   שם האפליקציה, כמו שהמשתמשים ייראו אותו
               </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputWithIcon
                icon={iconIcon}
                placeholder="אייקון"
                value={app.icon}
                onChange={(e) => setApp({ ...app, icon: e.target.value })}
                error={!!errors.icon}
                helperText={errors.icon}
                required
              />
               <Typography variant="caption" display="block" gutterBottom>
                   שם האייקון מתוך <a href="https://fontawesome.com/search?ip=classic&f=classic&s=solid&ic=free&o=r" target="_blank" rel="noopener noreferrer">font awesome</a>, בפורמט faImage
               </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputWithIcon
                icon={titleIcon}
                placeholder="תיאור"
                value={app.description}
                onChange={(e) => setApp({ ...app, description: e.target.value })}
              />
              <Typography variant="caption" display="block" gutterBottom>
                  תיאור קצר של האפליקציה
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputWithIcon
                icon={creditIcon}
                placeholder="קרדיט"
                value={app.credit}
                onChange={(e) => setApp({ ...app, credit: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownWithIcon
                icon={statusIcon}
                placeholder="סטטוס"
                value={app.status}
                onChange={(e) => setApp({ ...app, status: e.target.value })}
              >
                <option value="normal">רגיל</option>
                <option value="inDevelopment">בפיתוח</option>
                <option value="new">חדש</option>
              </DropdownWithIcon>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6">מנהלים</Typography>
              {app.admins.map((admin, index) => (
                <Paper key={index} style={{ padding: '16px', marginBottom: '16px', position: 'relative' }}>
                   <Button
                      onClick={() => removeAdmin(index)}
                      style={{ position: 'absolute', top: 0, left: 0, minWidth: 'auto', padding: '4px' }}
                    >
                      <X size={18} />
                    </Button>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DropdownWithIcon
                        icon={hospitalIcon}
                        placeholder="בית חולים"
                        value={admin.hospital?._id || admin.hospital}
                        onChange={(e) => handleAdminChange(index, 'hospital', e.target.value)}
                      >
                        {hospitals.map(h => (
                          <option key={h._id} value={h._id}>{h.nameHebrew}</option>
                        ))}
                      </DropdownWithIcon>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DropdownWithIcon
                        icon={userIcon}
                        placeholder="משתמש"
                        value={admin.user?._id || admin.user}
                        onChange={(e) => handleAdminChange(index, 'user', e.target.value)}
                        disabled={!admin.hospital}
                      >
                        {(usersByHospital[admin.hospital?._id || admin.hospital] || []).map(u => (
                          <option key={u._id} value={u._id}>{u.name}</option>
                        ))}
                      </DropdownWithIcon>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              <Button onClick={addAdmin} variant="outlined">הוסף מנהל</Button>
            </Grid>

            <Grid item xs={12}>
              <div className="form-actions">
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center' }}>
                   <LoadingSubmitButton
                       isLoading={isSubmitting}
                       text={id ? 'עדכן' : 'צור'}
                       loadingText={id ? 'מעדכן...' : 'יוצר...'}
                       style={primaryButtonStyle}
                   />
                   <Button
                       variant="outlined"
                       onClick={() => navigate('/apps-mgmt')}
                       style={secondaryButtonStyle}
                   >
                       ביטול
                   </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </form>
        <Loader isLoading={isLoading} />
      </div>
    </CommonLayout>
  );
};

export default AddEditApp;